import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { FleetStatus, ChartTooltipProps } from '@/types';

interface FleetStatusChartProps {
  data: FleetStatus;
}

const COLORS = {
  active: '#10B981',
  inactive: '#EF4444',
  maintenance: '#F59E0B',
};

const FleetStatusChart: React.FC<FleetStatusChartProps> = ({ data }) => {
  const chartData = [
    { name: 'Active', value: data.active, color: COLORS.active },
    { name: 'Maintenance', value: data.maintenance, color: COLORS.maintenance },
    { name: 'Inactive', value: data.inactive, color: COLORS.inactive },
  ];

  const CustomTooltip: React.FC<ChartTooltipProps> = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const d = payload[0];
      return (
        <div className="bg-navy-card border border-navy-border rounded-lg p-3 shadow-xl">
          <p className="text-xs text-text-heading font-medium">{d.name}</p>
          <p className="text-sm text-gold-primary font-bold">{d.value} agents</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-navy-card border border-navy-border rounded-xl p-6">
      <h3 className="text-base font-semibold text-text-heading mb-4">Fleet Status</h3>
      
      <div className="h-56 relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={85}
              paddingAngle={3}
              dataKey="value"
              strokeWidth={0}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
        
        {/* Center label */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <p className="text-2xl font-bold text-text-heading">{data.total}</p>
            <p className="text-[10px] text-text-muted uppercase tracking-wider">Agents</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center space-x-6 mt-4">
        {chartData.map((entry, i) => (
          <div key={i} className="flex items-center space-x-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-xs text-text-muted">{entry.name}</span>
            <span className="text-xs font-medium text-text-primary">{entry.value}</span>
          </div>
        ))}
      </div>

      {/* Operational % */}
      <div className="mt-4 pt-4 border-t border-navy-border/50 text-center">
        <p className="text-2xl font-bold text-emerald-400">
          {((data.active / data.total) * 100).toFixed(1)}%
        </p>
        <p className="text-[10px] text-text-muted uppercase tracking-wider">Operational</p>
      </div>
    </div>
  );
};

export default FleetStatusChart;
